import { useEffect, useRef, useState } from 'react';
import type { PadEngine } from '@/canvas/engine';
import { IconPlay, IconPause, IconClose } from '../icons';

/**
 * Timelapse playback. The op log already records the drawing in the order it
 * happened, so replaying it is just a matter of moving a cutoff through the
 * sequence numbers.
 */
export default function ReplayBar({ engine, onClose }: { engine: PadEngine; onClose: () => void }) {
  const timeline = useRef<number[]>([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);

  if (!timeline.current.length) timeline.current = engine.replayTimeline();
  const total = timeline.current.length;

  // Apply the current cutoff, and put the pad back when we unmount.
  useEffect(() => {
    engine.setReplayLimit(total ? timeline.current[Math.min(index, total - 1)] : null);
  }, [engine, index, total]);

  useEffect(() => () => engine.setReplayLimit(null), [engine]);

  useEffect(() => {
    if (!playing || !total) return undefined;
    // Aim for the whole drawing in about 12 seconds, at least 12ms a step.
    const step = Math.max(12, Math.round(12000 / Math.max(total, 1) / speed));
    const timer = window.setInterval(() => {
      setIndex((prev) => {
        if (prev >= total - 1) {
          setPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, step);
    return () => window.clearInterval(timer);
  }, [playing, total, speed]);

  useEffect(() => {
    engine.fitEverything();
  }, [engine]);

  if (!total) {
    return (
      <div className="hint-strip" style={{ bottom: 'calc(var(--safe-b) + 90px)' }}>
        Nothing has been drawn yet.
        <button type="button" className="linkbtn" style={{ marginLeft: 10 }} onClick={onClose}>Close</button>
      </div>
    );
  }

  return (
    <div
      className="pill"
      style={{
        position: 'absolute',
        bottom: 'calc(var(--safe-b) + 16px)',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '6px 10px',
        gap: 10,
        width: 'min(520px, calc(100vw - 40px))',
      }}
    >
      <button
        type="button"
        className="pill-btn pill-btn--sm"
        onClick={() => {
          if (index >= total - 1) setIndex(0);
          setPlaying((p) => !p);
        }}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? <IconPause size={18} /> : <IconPlay size={18} />}
      </button>

      <input
        type="range"
        className="grow"
        min={0}
        max={total - 1}
        value={index}
        onChange={(e) => { setPlaying(false); setIndex(Number(e.target.value)); }}
        aria-label="Replay position"
      />

      <button
        type="button"
        className="badge"
        onClick={() => setSpeed((s) => (s >= 4 ? 0.5 : s * 2))}
        title="Playback speed"
      >
        {speed}×
      </button>

      <span className="chip__meta" style={{ minWidth: 62, textAlign: 'right' }}>
        {index + 1}/{total}
      </span>

      <button type="button" className="pill-btn pill-btn--sm" onClick={onClose} aria-label="Exit replay">
        <IconClose size={18} />
      </button>
    </div>
  );
}
